/*
  Example questions for the ask box, chosen by the business's trade.

  The box used to offer "My best tech just asked for a 20% raise" to everybody. To a
  plumbing company that is a real Friday. To a restaurant it is a sign the product was
  built for somebody else, and it is the first thing an owner sees on the dashboard.

  Two rules for anything added here. Every line is a question an owner would actually
  type, with a number or a name in it where a real one would have one; a generic prompt
  teaches the owner to write generic questions, and a generic question gets a generic
  board. And the fallback set never mentions a van, a truck, a tech or a job, because it
  is what a business we have no match for will see.
*/

const UNIVERSAL = [
  'My best person just asked for a 20% raise.',
  'A competitor undercut me by 15%. Do I match it?',
  'Should I hire another person or wait another quarter?',
  'My margin fell from 34% to 22% and I do not know why.',
  'A big customer wants to pay in 60 days instead of 30.',
]

/*
  Matched on a substring rather than an exact value, because `trade` is free text an owner
  typed. "Italian restaurant", "restaurant / bar" and "restaurant" all have to land in the
  same place, and anything that matches nothing falls through to UNIVERSAL rather than to
  somebody else's world.
*/
const FAMILIES: { match: RegExp; prompts: string[] }[] = [
  {
    match: /restaurant|cafe|café|coffee|bakery|bar\b|pub|food|catering|kitchen|pizz|diner|bistro|grill|deli/i,
    prompts: [
      'My head chef just asked for a 20% raise.',
      'Food cost hit 34% and I cannot tell which station is bleeding.',
      'The delivery apps take 30%. Do I stay on them?',
      'Covers are up on Saturday but the average check is flat.',
      'Two line cooks quit in a month. Is it the pay or the schedule?',
    ],
  },
  {
    match: /plumb|hvac|electric|roof|construct|contract|landscap|excavat|paint|mechanic|garage|auto|trade|install|repair/i,
    prompts: [
      'My best tech just asked for a 20% raise.',
      'Two competitors dropped their callout fee. Do I match it?',
      'Should I buy the fourth van or hire another apprentice?',
      'My close rate fell from 34% to 22% and I do not know why.',
      'A customer wants net-60 terms on a 40k job.',
    ],
  },
  {
    match: /retail|shop|store|boutique|grocer|market|apparel|florist/i,
    prompts: [
      'My best salesperson just asked for a 20% raise.',
      'Foot traffic is down and online is up. Do I shrink the floor?',
      'A supplier raised prices 12%. Do I pass it on or eat it?',
      'Should I open a second location or go deeper in this one?',
      'Inventory turns fell and I cannot tell which category did it.',
    ],
  },
  {
    match: /salon|spa|barber|groom|fitness|gym|studio|clinic|dental|therap|veterinar|wellness/i,
    prompts: [
      'My most-booked stylist just asked for a 20% raise.',
      'My no-show rate is 18% and I have never charged for one.',
      'Should I add a room or add hours to the ones I have?',
      'Rebooking fell from 60% to 44% and I do not know why.',
      'A competitor opened four blocks away and undercut me.',
    ],
  },
]

/** The example questions for a trade, longest-lived first. Never empty. */
export function promptsFor(trade: string | null | undefined): string[] {
  if (!trade) return UNIVERSAL
  return FAMILIES.find(f => f.match.test(trade))?.prompts ?? UNIVERSAL
}

/**
 * One example, for a placeholder that only has room for a sentence. Stable for a given
 * trade rather than random, so the box does not appear to change its mind while the owner
 * is looking at it.
 */
export function promptFor(trade: string | null | undefined): string {
  return promptsFor(trade)[0]!
}
